import { motion } from 'framer-motion';
import { Globe, Shield, Zap } from 'lucide-react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import Header from '@/common/header';
import { FaqAccordion } from '@/components/trial/shared/faq-accordion';
import { PingCounter } from '@/components/trial/shared/ping-counter';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { outfit } from '@/public/assets/fonts/Outfit';

import { CreateAccountModal } from '../../create-account-modal';
import { Footer } from '../../footer';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { ArcadeGrid, HeroGlow, ScanlineOverlay, SpeedStreaks } from './components/backgrounds';
import { ACCENT_BLUE, DARK_BG, EVIL_RED, FAQ_ITEMS, KILL_STEPS, MUTED_GRAY, NOPING_GREEN, PRO_ENDORSEMENTS, TEXT_LIGHT, TOOL_CARDS, WEAPON_STATS } from './components/constants';
import { AnimatedNumber, BeforeAfterPanel, GameCarousel, GlitchTitle, ToolIcon } from './components/interactive';
import { AccentDivider, GeoCard, Reveal, Section, SectionTitle } from './components/ui-helpers';

interface LagIsDeadProps extends DefaultTrialProps {}

export function LagIsDead({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: LagIsDeadProps) {
  const { t } = useTranslation(['components/trial/lp/v2a']);
  const [modalOpen, setModalOpen] = useState(false);

  const faqItems = FAQ_ITEMS.map((_item, i) => ({
    q: t(`faq.items.${i}.q`),
    a: t(`faq.items.${i}.a`),
  }));

  return (
    <main className={`${bai_jamjuree.className} ${outfit.variable} relative min-h-screen`} style={{ background: DARK_BG, color: TEXT_LIGHT }}>
      <ArcadeGrid />
      <ScanlineOverlay />

      <Header variant='primary' />

      {/* SECTION 1 - HERO */}
      <section className='relative flex flex-col items-center justify-center overflow-hidden px-4 pb-12 pt-24 md:pb-16 md:pt-28'>
        <HeroGlow />
        <SpeedStreaks />

        <div className='relative z-10 mx-auto w-full max-w-7xl text-center'>
          <GlitchTitle />

          <motion.p className='mx-auto mt-4 max-w-2xl text-base leading-relaxed md:mt-6 md:text-lg lg:text-xl' style={{ fontFamily: 'var(--font-outfit)', color: MUTED_GRAY }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7, ease: 'easeOut' as const }}>
            {t('hero.subheadline')}
          </motion.p>

          <motion.div className='mt-4 md:mt-6' initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 1.0, ease: 'easeOut' as const }}>
            <PingCounter startPing={180} endPing={5} duration={2.5} size='lg' accentColor={NOPING_GREEN} labelOptimizing={t('ping.optimizing')} labelDone={t('ping.done')} />
          </motion.div>

          <motion.div className='mt-4 md:mt-6' initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.2, ease: 'easeOut' as const }}>
            <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='cursor-pointer rounded-lg border-none px-8 py-4 text-lg font-bold uppercase tracking-[0.2em]' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 15px ${NOPING_GREEN}30` }} whileHover={{ scale: 1.05, boxShadow: `0 0 25px ${NOPING_GREEN}50` }} whileTap={{ scale: 0.95 }}>
              {t('hero.cta')}
            </motion.button>
            <p className='mt-3 text-sm' style={{ color: MUTED_GRAY }}>
              {t('hero.cta_subtext')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 - STATS */}
      <Section id='stats'>
        <AccentDivider color={NOPING_GREEN} />
        <div className='grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6'>
          {WEAPON_STATS.map((stat, i) => (
            <GeoCard key={stat.label} delay={i * 0.12} glow={stat.color}>
              <div className='flex min-h-[180px] flex-col items-center justify-center p-6 text-center md:min-h-[220px] md:p-8'>
                <ToolIcon name={stat.icon} className='mx-auto mb-3 size-5 opacity-40 md:size-6' style={{ color: stat.color }} />
                {stat.label === 'PING REDUCTION' ? (
                  <div className='leading-none' style={{ fontFamily: 'var(--font-bai)', color: stat.color }}>
                    <div className='text-lg font-semibold uppercase tracking-wider opacity-70 md:text-xl'>{t('stats.up_to')}</div>
                    <div className='text-5xl font-bold md:text-6xl lg:text-7xl'>
                      <AnimatedNumber target={stat.value} suffix={stat.suffix} duration={1.8} />
                    </div>
                  </div>
                ) : (
                  <div className='text-5xl font-bold leading-none md:text-6xl lg:text-7xl' style={{ fontFamily: 'var(--font-bai)', color: stat.color }}>
                    <AnimatedNumber target={stat.value} suffix={stat.suffix} duration={1.8} />
                  </div>
                )}
                <div className='mt-3 text-[10px] font-semibold tracking-[0.25em] md:text-xs' style={{ fontFamily: 'var(--font-outfit)', color: MUTED_GRAY }}>
                  {t(`stats.labels.${stat.label}`)}
                </div>
              </div>
            </GeoCard>
          ))}
        </div>
      </Section>

      {/* SECTION 3 - BEFORE / AFTER */}
      <Section id='before-after'>
        <AccentDivider color={EVIL_RED} />
        <SectionTitle title={t('before_after.title')} titleAccent={t('before_after.title_accent')} accentColor={NOPING_GREEN} subtitle={t('before_after.subtitle')} />
        <BeforeAfterPanel />
        <Reveal delay={0.5} className='mt-12 text-center md:mt-16'>
          <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='cursor-pointer rounded-lg border-none px-8 py-4 text-base font-bold uppercase tracking-[0.2em]' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 15px ${NOPING_GREEN}30` }} whileHover={{ scale: 1.05, boxShadow: `0 0 25px ${NOPING_GREEN}50` }} whileTap={{ scale: 0.95 }}>
            {t('before_after.cta')}
          </motion.button>
          <p className='mt-3 text-sm' style={{ color: MUTED_GRAY }}>
            {t('before_after.cta_subtext')}
          </p>
        </Reveal>
      </Section>

      {/* SECTION 4 - ARSENAL */}
      <Section id='arsenal'>
        <AccentDivider color={ACCENT_BLUE} />
        <SectionTitle title={t('arsenal.title')} titleAccent={t('arsenal.title_accent')} accentColor={NOPING_GREEN} subtitle={t('arsenal.subtitle')} />
        <div className='mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3'>
          {TOOL_CARDS.map((tool, i) => (
            <GeoCard key={tool.name} delay={i * 0.08} glow={tool.color}>
              <div className='p-6 md:p-8'>
                <div className='mb-4 flex items-center justify-between'>
                  <div className='flex size-12 items-center justify-center rounded-lg border transition-all duration-300' style={{ borderColor: 'rgba(255,255,255,0.06)', background: `${tool.color}08` }}>
                    <ToolIcon name={tool.icon} className='size-5' style={{ color: tool.color }} />
                  </div>
                  <span className='text-2xl font-bold opacity-20 transition-opacity group-hover:opacity-60 md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color: tool.color }}>
                    {t(`arsenal.tools.${i}.stat`)}
                  </span>
                </div>
                <h3 className='mb-2 text-lg font-bold tracking-wide text-white md:text-xl' style={{ fontFamily: 'var(--font-bai)' }}>
                  {t(`arsenal.tools.${i}.name`)}
                </h3>
                <p className='text-sm leading-relaxed' style={{ fontFamily: 'var(--font-outfit)', color: MUTED_GRAY }}>
                  {t(`arsenal.tools.${i}.desc`)}
                </p>
              </div>
            </GeoCard>
          ))}
        </div>
      </Section>

      {/* SECTION 5 - 3 STEPS */}
      <Section id='steps'>
        <AccentDivider color={NOPING_GREEN} />
        <SectionTitle title='' titleAccent='' accentColor={EVIL_RED} subtitle={t('steps.subtitle')} />
        <div className='-mt-16 mb-16 text-center'>
          <Reveal>
            <h2 className='text-4xl font-bold tracking-[-0.02em] md:text-6xl lg:text-7xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
              <span style={{ color: EVIL_RED }}>{t('steps.heading_accent')}</span> {t('steps.heading')}
            </h2>
          </Reveal>
        </div>

        <div className='mx-auto flex max-w-5xl flex-col gap-4 md:flex-row md:gap-0'>
          {KILL_STEPS.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.15} className='relative flex-1'>
              <div
                className='relative h-full overflow-hidden p-8 md:p-10'
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  borderTop: `2px solid ${step.accent}33`,
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  borderInlineStart: i === 0 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  borderInlineEnd: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <div className='absolute -start-2 -top-6 select-none text-[100px] font-bold leading-none opacity-[0.04] md:text-[120px]' style={{ fontFamily: 'var(--font-bai)', color: step.accent }}>
                  {step.num}
                </div>
                <div className='relative z-10'>
                  <div className='mb-5 inline-flex items-center gap-2 px-3 py-1 text-xs font-bold tracking-[0.2em]' style={{ background: `${step.accent}15`, color: step.accent, border: `1px solid ${step.accent}30` }}>
                    {t('steps.step_label')} {step.num}
                  </div>
                  <h3 className='mb-3 text-2xl font-bold tracking-widest md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color: step.accent }}>
                    {t(`steps.items.${i}.title`)}
                  </h3>
                  <p className='text-sm leading-relaxed' style={{ fontFamily: 'var(--font-outfit)', color: MUTED_GRAY }}>
                    {t(`steps.items.${i}.desc`)}
                  </p>
                </div>
                {i < KILL_STEPS.length - 1 && (
                  <div className='absolute -end-3 top-1/2 z-20 hidden size-6 -translate-y-1/2 md:block'>
                    <div className='size-full rotate-45 border-e-2 border-t-2' style={{ borderColor: step.accent }} />
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 6 - PRO ENDORSEMENTS */}
      <Section id='pros'>
        <AccentDivider color={EVIL_RED} />
        <SectionTitle title={t('pros.title')} titleAccent={t('pros.title_accent')} accentColor={NOPING_GREEN} subtitle={t('pros.subtitle')} />
        <div className='mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-3 md:gap-5'>
          {PRO_ENDORSEMENTS.slice(0, 3).map((pro, i) => (
            <GeoCard key={`${pro.name}-${i}`} delay={i * 0.12} glow={NOPING_GREEN}>
              <div className='flex h-full flex-col p-6 md:p-8'>
                <div className='mb-4 select-none text-5xl font-bold leading-none md:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: NOPING_GREEN, opacity: 0.15 }}>
                  &ldquo;
                </div>
                <blockquote className='mb-6 text-sm italic leading-relaxed md:text-base' style={{ fontFamily: 'var(--font-outfit)', color: TEXT_LIGHT }}>
                  {t(`pros.endorsements.${i}.quote`)}
                </blockquote>
                <div className='mt-auto flex items-center gap-3 pt-4' style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className='size-10 shrink-0 overflow-hidden rounded-full border-2' style={{ borderColor: `${NOPING_GREEN}40` }}>
                    <Image src={pro.image} alt={pro.name} width={40} height={40} className='size-full object-cover' />
                  </div>
                  <div>
                    <div className='text-sm font-bold text-white' style={{ fontFamily: 'var(--font-bai)' }}>
                      {pro.name}
                    </div>
                    <div className='text-xs' style={{ fontFamily: 'var(--font-outfit)', color: MUTED_GRAY }}>
                      {t(`pros.endorsements.${i}.title`)} &bull; {t(`pros.endorsements.${i}.game`)}
                    </div>
                  </div>
                </div>
              </div>
            </GeoCard>
          ))}
        </div>
      </Section>

      {/* SECTION 7 - GAMES + TRUST */}
      <Section id='games'>
        <AccentDivider color={ACCENT_BLUE} />
        <SectionTitle title={t('games_section.title')} titleAccent={t('games_section.title_accent')} accentColor={NOPING_GREEN} subtitle={t('games_section.subtitle')} />
        <Reveal delay={0.25} className='mx-auto max-w-5xl'>
          <GameCarousel />
        </Reveal>
        <Reveal delay={0.35} className='mt-12'>
          <div className='flex items-center justify-center gap-6 text-xs tracking-widest' style={{ fontFamily: 'var(--font-outfit)', color: 'rgba(255,255,255,0.3)' }}>
            <span className='flex items-center gap-1.5'>
              <Shield className='size-3' /> {t('trust.safe')}
            </span>
            <span className='size-1 rounded-full bg-white/10' />
            <span className='flex items-center gap-1.5'>
              <Zap className='size-3' /> {t('trust.no_bans')}
            </span>
            <span className='size-1 rounded-full bg-white/10' />
            <span className='flex items-center gap-1.5'>
              <Globe className='size-3' /> {t('trust.countries')}
            </span>
          </div>
        </Reveal>
      </Section>

      {/* SECTION 8 - FAQ */}
      <Section id='faq'>
        <AccentDivider color={`${NOPING_GREEN}80`} />
        <SectionTitle title={t('faq.title')} titleAccent={t('faq.title_accent')} accentColor={EVIL_RED} subtitle={t('faq.subtitle')} />
        <Reveal delay={0.25} className='mx-auto max-w-3xl'>
          <FaqAccordion items={faqItems} accentColor={NOPING_GREEN} itemClassName='border-none rounded-none' triggerClassName='text-base md:text-lg font-bold tracking-wide' contentClassName='leading-relaxed' />
        </Reveal>
      </Section>

      {/* SECTION 9 - FINAL CTA */}
      <section className='relative overflow-hidden px-4 py-28 md:py-40'>
        <div className='absolute inset-0' style={{ background: `linear-gradient(135deg, ${DARK_BG} 0%, #0a1a04 20%, #132808 40%, #1a3a0a 60%, ${NOPING_GREEN} 200%)` }} />
        <div className='absolute inset-0' style={{ background: `radial-gradient(ellipse at center, ${NOPING_GREEN}26, transparent 70%)` }} />
        <div className='absolute inset-0 opacity-[0.06]' style={{ backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 15px, ${NOPING_GREEN}4D 15px, ${NOPING_GREEN}4D 16px)` }} />
        <div className='absolute inset-x-0 top-0 h-32' style={{ background: `linear-gradient(to bottom, ${DARK_BG}, transparent)` }} />

        <div className='relative z-10 mx-auto max-w-4xl text-center'>
          <Reveal>
            <h2 className='mb-6 text-5xl font-bold leading-[0.85] md:text-7xl lg:text-[100px]' style={{ fontFamily: 'var(--font-bai)' }}>
              <span style={{ color: EVIL_RED }}>{t('final_cta.stop')}</span> <span className='text-white'>{t('final_cta.losing')}</span>
              <br />
              <span className='text-white'>{t('final_cta.to')} </span>
              <span style={{ background: `linear-gradient(90deg, ${NOPING_GREEN}, ${ACCENT_BLUE})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('final_cta.lag')}</span>
            </h2>
          </Reveal>

          <Reveal delay={0.15}>
            <p className='mb-10 text-lg font-medium tracking-wide md:text-xl' style={{ fontFamily: 'var(--font-outfit)', color: 'rgba(255,255,255,0.6)' }}>
              {t('final_cta.subtitle')}
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='cursor-pointer rounded-lg border-none px-8 py-4 text-lg font-bold uppercase tracking-[0.2em]' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 15px ${NOPING_GREEN}30` }} whileHover={{ scale: 1.05, boxShadow: `0 0 25px ${NOPING_GREEN}50` }} whileTap={{ scale: 0.95 }}>
              {t('final_cta.cta')}
            </motion.button>
            <p className='mt-3 text-sm' style={{ color: MUTED_GRAY }}>
              {t('final_cta.cta_subtext')}
            </p>
          </Reveal>

          <Reveal delay={0.4}>
            <div className='mt-10 flex items-center justify-center gap-6 text-xs tracking-widest' style={{ fontFamily: 'var(--font-outfit)', color: 'rgba(255,255,255,0.3)' }}>
              <span className='flex items-center gap-1.5'>
                <Shield className='size-3' /> {t('trust.safe')}
              </span>
              <span className='size-1 rounded-full bg-white/10' />
              <span className='flex items-center gap-1.5'>
                <Zap className='size-3' /> {t('trust.no_bans')}
              </span>
              <span className='size-1 rounded-full bg-white/10' />
              <span className='flex items-center gap-1.5'>
                <Globe className='size-3' /> {t('trust.countries')}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />

      <CreateAccountModal open={modalOpen} onClose={() => setModalOpen(false)} daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v2a-modal-form' />
    </main>
  );
}
